// components/NumberSpinner.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, TextField, useMediaQuery } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';

interface NumberSpinnerNoKeyboardProps {
  value: number | null;
  onChange: (newValue: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  isOneDigit?: boolean;
}

const NumberSpinnerNoKeyboard: React.FC<NumberSpinnerNoKeyboardProps> = ({
  value = null,
  onChange,
  min = -Infinity,
  max = Infinity,
  step = 1,
  disabled = false,
  isOneDigit = false,
}) => {
  const [displayValue, setDisplayValue] = useState<string>(
    value !== null ? formatNumber(value, step) : ''
  );
  const [isTyping, setIsTyping] = useState(false);
  const holdTimeout = useRef<NodeJS.Timeout | null>(null);
  const holdInterval = useRef<NodeJS.Timeout | null>(null);
  const applyTimeout = useRef<NodeJS.Timeout | null>(null);
  const holdValueRef = useRef<number>(0);

  const isMobile = useMediaQuery('(max-width:1250px)');

  const clampWithStep = (v: number): number => {
    if (step <= 0) {
      return Math.min(max, Math.max(min, v));
    }

    const decimals = (step.toString().split('.')[1] || '').length;
    const factor = Math.pow(10, decimals);

    const vInt = Math.round(v * factor);
    const stepInt = Math.round(step * factor);

    const nearestMultiple = Math.round(vInt / stepInt) * stepInt;
    let result = nearestMultiple / factor;

    result = parseFloat(result.toFixed(decimals));

    return Math.min(max, Math.max(min, result));
  };

  const parseDecimal = (str: string): number | null => {
    const cleaned = str.trim().replace(/,/g, '.');
    if (cleaned === '' || cleaned === '-' || cleaned === '.') {
      return null;
    }
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  function formatNumber(num: number, stepVal: number): string {
    if (stepVal % 1 === 0) {
      return Math.round(num).toString();
    }
    const decimalPlaces = stepVal.toString().split('.')[1]?.length || 0;
    return num.toFixed(decimalPlaces);
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;

    const isValidInput = /^-?\d*\.?\d*$/.test(rawValue);
    if (!isValidInput && rawValue !== '') {
      return;
    }

    setDisplayValue(rawValue);
    setIsTyping(true);

    if (applyTimeout.current) {
      clearTimeout(applyTimeout.current);
      applyTimeout.current = null;
    }

    if (rawValue.trim() === '') {
      return;
    }

    const numValue = parseDecimal(rawValue);
    if (numValue === null) {
      return;
    }

    applyTimeout.current = setTimeout(() => {
      setIsTyping(false);

      const finalNum = parseDecimal(rawValue);
      if (finalNum !== null) {
        const clamped = clampWithStep(finalNum);
        const displayStr = formatNumber(clamped, step);
        setDisplayValue(displayStr);
        if (clamped !== value) {
          onChange(clamped);
        }
      }
      applyTimeout.current = null;
    }, 1000);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setIsTyping(false);
      if (applyTimeout.current) {
        clearTimeout(applyTimeout.current);
        applyTimeout.current = null;
      }

      if (displayValue.trim() === '') {
        (event.target as HTMLInputElement).blur();
        return;
      }

      const numValue = parseDecimal(displayValue);
      if (numValue !== null) {
        const clamped = clampWithStep(numValue);
        const displayStr = formatNumber(clamped, step);
        setDisplayValue(displayStr);
        if (clamped !== value) {
          onChange(clamped);
        }
      }
      (event.target as HTMLInputElement).blur();
    }
    if (event.key === 'Escape') {
      setIsTyping(false);
      if (applyTimeout.current) {
        clearTimeout(applyTimeout.current);
        applyTimeout.current = null;
      }
      setDisplayValue(value !== null ? formatNumber(value, step) : '');
      (event.target as HTMLInputElement).blur();
    }
  };

  const handleBlur = () => {
    setIsTyping(false);
    if (applyTimeout.current) {
      clearTimeout(applyTimeout.current);
      applyTimeout.current = null;
    }

    if (displayValue.trim() === '') {
      return;
    }

    const numValue = parseDecimal(displayValue);
    if (numValue !== null) {
      const clamped = clampWithStep(numValue);
      const displayStr = formatNumber(clamped, step);
      setDisplayValue(displayStr);
      if (clamped !== value) {
        onChange(clamped);
      }
    }
  };

  const startHold = (direction: 'inc' | 'dec') => {
    stopHold();

    setIsTyping(false);
    if (applyTimeout.current) {
      clearTimeout(applyTimeout.current);
      applyTimeout.current = null;
    }

    const currentValue = parseDecimal(displayValue) ?? 0;
    holdValueRef.current = currentValue;

    const action = () => {
      const newValue = holdValueRef.current + (direction === 'inc' ? step : -step);
      const clamped = clampWithStep(newValue);
      holdValueRef.current = clamped;

      const displayStr = formatNumber(clamped, step);
      setDisplayValue(displayStr);

      if (clamped !== value) {
        onChange(clamped);
      }
    };

    action();

    const delay = isMobile ? 400 : 300;
    holdTimeout.current = setTimeout(() => {
      holdInterval.current = setInterval(action, isMobile ? 80 : 60);
    }, delay);
  };

  const stopHold = () => {
    if (holdTimeout.current) {
      clearTimeout(holdTimeout.current);
      holdTimeout.current = null;
    }
    if (holdInterval.current) {
      clearInterval(holdInterval.current);
      holdInterval.current = null;
    }
  };

  useEffect(() => {
    if (isTyping) return;

    if (value === null) {
      setDisplayValue('');
      return;
    }

    setDisplayValue(formatNumber(value, step));
  }, [value, step, isTyping]);

  useEffect(() => {
    return () => {
      if (holdTimeout.current) clearTimeout(holdTimeout.current);
      if (holdInterval.current) clearInterval(holdInterval.current);
      if (applyTimeout.current) clearTimeout(applyTimeout.current);
    };
  }, []);

  const currentNum = parseDecimal(displayValue);
  const isDecrementDisabled = disabled || currentNum === null || (min !== -Infinity && currentNum <= min);
  const isIncrementDisabled = disabled || currentNum === null || (max !== Infinity && currentNum >= max);

  const renderValueDisplay = () => {
    if (isMobile) {
      return (
        <Box
          sx={{
            width: isOneDigit ? '.75rem' : '32px',
            textAlign: 'center',
            padding: '2px 0',
            fontSize: '0.7rem',
            color: 'var(--text-color)',
            bgcolor: 'transparent',
            fontFamily: 'monospace',
            fontWeight: 500,
            userSelect: 'none',
          }}
        >
          {displayValue}
        </Box>
      );
    }

    return (
      <TextField
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        variant="standard"
        size="small"
        InputProps={{
          disableUnderline: true,
          sx: {
            width: isOneDigit ? '.75rem' : '32px',
            '& input': {
              textAlign: 'center',
              padding: '2px 0',
              fontSize: '0.7rem',
              color: 'var(--text-color)',
              bgcolor: 'transparent',
              fontFamily: 'monospace',
              fontWeight: 500,
              '&.Mui-disabled': {
                WebkitTextFillColor: 'dimgray',
              },

              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              '&[type=number]': {
                MozAppearance: 'textfield',
              },
              border: 'none',
              outline: 'none',
            },
          },
        }}
        disabled={disabled}
      />
    );
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: 'var(--input-bg)',
        borderRadius: '20px',
        padding: '1px',
        border: '1px solid var(--input-border)',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <IconButton
        size="small"
        {...(!isMobile && {
          onPointerDown: (e) => {
            e.preventDefault();
            startHold('dec');
          },
          onPointerUp: stopHold,
          onPointerLeave: stopHold,
          onPointerCancel: stopHold,
        })}
        {...(isMobile && {
          onTouchStart: (e) => {
            e.preventDefault();
            startHold('dec');
          },
          onTouchEnd: stopHold,
        })}
        disabled={isDecrementDisabled}
        sx={{
          bgcolor: 'transparent',
          color: 'var(--toolbar-button-border)',
          width: 24,
          height: 24,
          borderRadius: '12px',
          '&:hover': {
            bgcolor: 'rgba(20, 192, 255, 0.15)',
          },
          '&.Mui-disabled': {
            color: 'rgba(20, 235, 255, 0.3)',
          },
          touchAction: 'manipulation',
        }}
      >
        <Remove sx={{ fontSize: 14 }} />
      </IconButton>

      {renderValueDisplay()}

      <IconButton
        size="small"
        {...(!isMobile && {
          onPointerDown: (e) => {
            e.preventDefault();
            startHold('inc');
          },
          onPointerUp: stopHold,
          onPointerLeave: stopHold,
          onPointerCancel: stopHold,
        })}
        {...(isMobile && {
          onTouchStart: (e) => {
            e.preventDefault();
            startHold('inc');
          },
          onTouchEnd: stopHold,
        })}
        disabled={isIncrementDisabled}
        sx={{
          bgcolor: 'transparent',
          color: 'var(--toolbar-button-border)',
          width: 24,
          height: 24,
          borderRadius: '12px',
          '&:hover': {
            bgcolor: 'rgba(20, 192, 255, 0.15)',
          },
          '&.Mui-disabled': {
            color: 'rgba(20, 235, 255, 0.3)',
          },
          touchAction: 'manipulation',
        }}
      >
        <Add sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
};

export default NumberSpinnerNoKeyboard;
